'use client';

import { useParams, useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import {
  getStepBySlug,
  getVisibleStepIndex,
  getVisibleSteps,
  INTAKE_STEPS,
  TIntakeStep,
  TIntakeStepValidationResult,
} from '../config/steps';
import { useIntakeWizardContext } from '../contexts/WizardProvider';

type UseWizardNavigationResult = {
  currentStep: TIntakeStep | undefined;
  currentStepIndex: number;
  visibleSteps: TIntakeStep[];
  hasNextStep: boolean;
  hasPreviousStep: boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (slug: string) => void;
  goToQuote: () => void;
  validateAndCompleteStep: () => TIntakeStepValidationResult;
  isStepCompleted: (stepId: string) => boolean;
  canNavigateToStep: (slug: string) => boolean;
};

export const useWizardNavigation = (): UseWizardNavigationResult => {
  const { state, dispatch } = useIntakeWizardContext();
  const router = useRouter();
  const { step: stepSlug } = useParams<{ step: string | undefined }>();

  // Get visible steps based on current state
  const visibleSteps = useMemo(() => getVisibleSteps(state), [state]);

  const currentStep = useMemo(() => {
    if (!stepSlug) return undefined;

    return getStepBySlug(stepSlug);
  }, [stepSlug]);

  const currentStepIndex = useMemo(() => {
    if (!stepSlug) return -1;

    return getVisibleStepIndex(stepSlug, state);
  }, [stepSlug, state]);

  const hasNextStep = useMemo(
    () => currentStepIndex >= 0 && currentStepIndex < visibleSteps.length - 1,
    [currentStepIndex, visibleSteps.length]
  );

  const hasPreviousStep = useMemo(
    () => currentStepIndex > 0,
    [currentStepIndex]
  );

  const isStepCompleted = useCallback(
    (stepId: string) => state.completedSteps.includes(stepId),
    [state.completedSteps]
  );

  // Check if we can navigate to a specific step
  const canNavigateToStep = useCallback(
    (slug: string) => {
      const targetIndex = getVisibleStepIndex(slug, state);
      if (targetIndex < 0) return false;

      for (let i = 0; i < targetIndex; i++) {
        const step = visibleSteps[i];

        if (!state.completedSteps.includes(step.id)) {
          return false;
        }
      }

      return true;
    },
    [state, visibleSteps]
  );

  // Validate and complete the current step
  const validateAndCompleteStep =
    useCallback((): TIntakeStepValidationResult => {
      if (!currentStep) {
        return { isValid: false, errors: { step: 'No current step' } };
      }

      const result = currentStep.validate(state);

      if (result.isValid) {
        const currentIndex = INTAKE_STEPS.indexOf(currentStep);

        dispatch({ type: 'COMPLETE_STEP', payload: currentStep.id });

        for (let i = currentIndex + 1; i < INTAKE_STEPS.length; i++) {
          const next = INTAKE_STEPS[i];

          if (
            !next.parent &&
            next.id !== 'pediatrics-upsell' &&
            !next.isVisible(state)
          ) {
            dispatch({ type: 'COMPLETE_STEP', payload: next.id });
          }
        }
      }

      return result;
    }, [currentStep, state, dispatch]);

  // Navigate to next step
  // Note: Step components handle their own validation before calling this.
  // We just mark the step complete and navigate.
  const goToNextStep = useCallback(() => {
    if (currentStep) {
      dispatch({ type: 'COMPLETE_STEP', payload: currentStep.id });

      for (let i = currentStepIndex + 1; i < INTAKE_STEPS.length; i++) {
        const next = INTAKE_STEPS[i];

        if (
          !next.parent &&
          next.id !== 'pediatrics-upsell' &&
          !next.isVisible(state)
        ) {
          dispatch({ type: 'COMPLETE_STEP', payload: next.id });
        }
      }
    }
    dispatch({ type: 'SET_ERROR', payload: null });

    if (hasNextStep) {
      const nextStep = visibleSteps[currentStepIndex + 1];
      router.push(`/get-started/${nextStep.slug}`);
    } else {
      // All steps completed, go to quote
      router.push('/get-started/quote');
    }
  }, [
    currentStep,
    hasNextStep,
    visibleSteps,
    currentStepIndex,
    router,
    state,
    dispatch,
  ]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    if (!hasPreviousStep) return;

    const prevStep = visibleSteps[currentStepIndex - 1];
    router.push(`/get-started/${prevStep.slug}`);
  }, [hasPreviousStep, visibleSteps, currentStepIndex, router]);

  // Navigate to a specific step
  const goToStep = useCallback(
    (slug: string) => {
      if (!canNavigateToStep(slug)) {
        console.warn(`Cannot navigate to step: ${slug}`);
        return;
      }
      router.push(`/get-started/${slug}`);
    },
    [canNavigateToStep, router]
  );

  const goToQuote = useCallback(
    () => router.push('/get-started/quote'),
    [router]
  );

  return {
    currentStep,
    currentStepIndex,
    visibleSteps,
    hasNextStep,
    hasPreviousStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    goToQuote,
    validateAndCompleteStep,
    isStepCompleted,
    canNavigateToStep,
  };
};
